/**
 * Builds the new selected-values array for a shift-click range selection in a
 * controlled `TValue[]` model (mirrors `buildShiftClickSelection` in
 * ViewTable.lib.ts, adapted from a RowSelectionState record to an array).
 *
 * Resolves the anchor to an index in the flat, visible, selectable order
 * (falls back to the clicked index when the anchor is missing), takes the
 * inclusive range between anchor and clicked, then toggles: if every value in
 * the range is already selected (or is the anchor) the range is removed;
 * otherwise it is added. Values outside the range keep their selection.
 */
export function buildRangeSelection<TValue extends string | number>({
    orderedValues,
    anchorValue,
    clickedValue,
    selected,
}: {
    orderedValues: TValue[];
    anchorValue: TValue | null;
    clickedValue: TValue;
    selected: TValue[];
}): TValue[] {
    const clickedIndex = orderedValues.indexOf(clickedValue);
    if (clickedIndex === -1) {
        return selected;
    }

    const anchorIndex =
        anchorValue != null ? orderedValues.indexOf(anchorValue) : -1;
    const resolvedAnchor = anchorIndex !== -1 ? anchorIndex : clickedIndex;
    const from = Math.min(resolvedAnchor, clickedIndex);
    const to = Math.max(resolvedAnchor, clickedIndex);

    const rangeValues = orderedValues.slice(from, to + 1);
    const selectedSet = new Set(selected);
    const isAllSelected = rangeValues.every(
        (v) => selectedSet.has(v) || v === anchorValue,
    );

    if (isAllSelected) {
        const rangeSet = new Set(rangeValues);
        return selected.filter((v) => !rangeSet.has(v));
    }

    const additions = rangeValues.filter((v) => !selectedSet.has(v));
    return [...selected, ...additions];
}
